import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { useAuthedApi } from "../lib/api";
import MyListings from "../components/dashboard/MyListings";
import Leads from "../components/dashboard/Leads";
import StoreEditor from "../components/dashboard/StoreEditor";
import SavedCars from "../components/dashboard/SavedCars";
import VerificationCard from "../components/dashboard/VerificationCard";
import ProfileEditor from "../components/dashboard/ProfileEditor";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";

export default function Dashboard() {
  const api = useAuthedApi();
  const [params, setParams] = useSearchParams();
  const [me, setMe] = useState(null);
  const tab = params.get("tab") || "listings";
  useEffect(() => { api("get", "/me").then(setMe); }, [api]); // eslint-disable-line

  const toggleAlerts = async (on) => {
    setMe({ ...me, lead_alerts_off: !on });
    try { await api("put", "/me/lead-alerts", { enabled: on }); toast.success(on ? "Lead email alerts on" : "Lead email alerts off"); }
    catch { setMe({ ...me, lead_alerts_off: on }); toast.error("Could not update"); }
  };
  const trig = "data-[state=active]:text-black data-[state=active]:bg-[#D4AF37] rounded-full px-4 py-2 text-xs sm:text-sm text-gray-300";
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32" data-testid="dashboard-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div><p className="eyebrow mb-3">My account {me?.role === "admin" && "· Admin"}</p><h1 className="font-display text-5xl sm:text-6xl">Dashboard</h1></div>
        <div className="flex gap-3">
          {me?.role === "admin" && <Link to="/admin" className="btn-outline" data-testid="dashboard-admin-link">Admin Panel</Link>}
          <Link to="/sell" className="btn-gold" data-testid="dashboard-new-listing">+ New listing</Link>
        </div>
      </div>
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
        <TabsList className="bg-[#111319] border gold-line rounded-full p-1 h-auto flex-wrap justify-start mb-8">
          <TabsTrigger value="listings" className={trig} data-testid="tab-listings">My Listings</TabsTrigger>
          <TabsTrigger value="saved" className={trig} data-testid="tab-saved">Saved Cars</TabsTrigger>
          <TabsTrigger value="received" className={trig} data-testid="tab-received">Offers & Enquiries</TabsTrigger>
          <TabsTrigger value="sent" className={trig} data-testid="tab-sent">Sent by me</TabsTrigger>
          <TabsTrigger value="store" className={trig} data-testid="tab-store">{me?.dealer ? "My Dealer Store" : "Become a Dealer"}</TabsTrigger>
          <TabsTrigger value="profile" className={trig} data-testid="tab-profile">My Profile</TabsTrigger>
        </TabsList>
        <TabsContent value="listings"><MyListings /></TabsContent>
        <TabsContent value="saved"><SavedCars /></TabsContent>
        <TabsContent value="received">
          {me && (
            <label className="lux-card p-4 mb-5 flex items-center justify-between gap-4 cursor-pointer max-w-xl" data-testid="lead-alerts-toggle-row">
              <span className="text-sm"><span className="font-semibold block">Email me about new leads</span><span className="text-gray-400 text-xs">Instant email to {me.email || "your account email"} for every new enquiry or offer</span></span>
              <Switch className="data-[state=unchecked]:bg-gray-600" checked={!me.lead_alerts_off} onCheckedChange={toggleAlerts} data-testid="lead-alerts-toggle" />
            </label>
          )}
          <Leads kind="received" />
        </TabsContent>
        <TabsContent value="sent"><Leads kind="sent" /></TabsContent>
        <TabsContent value="profile">{me && <ProfileEditor me={me} onSaved={setMe} />}</TabsContent>
        <TabsContent value="store">
          {me && (
            <div className="space-y-6">
              {me.dealer && <VerificationCard dealer={me.dealer} onSaved={(d) => setMe({ ...me, dealer: d })} />}
              <StoreEditor dealer={me.dealer} onSaved={(d) => setMe({ ...me, dealer: d })} />
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
